import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Clock,
  FileCheck,
  FileX,
  Play,
  UserX,
  type LucideIcon,
} from "lucide-react";
import type { DashboardStats } from "@/hooks/useDashboardQuery";

/**
 * StatCard-Definitionen für die rollenspezifischen Dashboards (TES-1093).
 *
 * Bewusst dieselben testIds, Farben, Werte und Ziel-Routen wie die
 * (filter-gekoppelten) Kachel-Arrays in Dashboard.tsx — bestehende E2E-Specs
 * (counter-updates, dashboard-stats) asserten `inspection-tile-*` /
 * `defect-tile-*` auf '/' auch für tester/mangelbearbeiter. Die rollenspezifischen
 * Sichten haben keine Filterleiste, daher hier statische Routen statt
 * buildInspectionsRoute/buildDefectsRoute.
 */

export interface RoleStatCardDef {
  name: string;
  value: number;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  testId: string;
  onClick: () => void;
}

type Translate = (key: string) => string;
type Navigate = (to: string) => void;

export function buildInspectionTileDefs(opts: {
  stats: DashboardStats | null | undefined;
  t: Translate;
  navigate: Navigate;
  /** unassigned-Kachel (Dashboard.tsx: QM || tester). */
  includeUnassigned: boolean;
}): RoleStatCardDef[] {
  const { stats, t, navigate, includeUnassigned } = opts;
  if (!stats) return [];
  const open = stats.inspectionsNotStarted + stats.inspectionsActive;
  const go = (status?: string) => () =>
    navigate(status ? `/inspections?status=${status}` : "/inspections");

  return [
    {
      name: t("common.total"),
      value: stats.totalInspections,
      icon: ClipboardList,
      color: "text-foreground",
      bgColor: "bg-muted",
      testId: "inspection-tile-total",
      onClick: go(),
    },
    {
      name: t("dashboard.openAll"),
      value: open,
      icon: AlertCircle,
      color: "text-warning-foreground",
      bgColor: "bg-warning/15 dark:bg-warning/20",
      testId: "inspection-tile-open",
      onClick: go("open"),
    },
    {
      name: t("dashboard.notStarted"),
      value: stats.inspectionsNotStarted,
      icon: FileCheck,
      color: "text-warning",
      bgColor: "bg-warning/10",
      testId: "inspection-tile-pending",
      onClick: go("pending"),
    },
    {
      name: t("dashboard.inProgress"),
      value: stats.inspectionsActive,
      icon: Play,
      color: "text-primary",
      bgColor: "bg-primary/10",
      testId: "inspection-tile-in_progress",
      onClick: go("in_progress"),
    },
    {
      name: t("dashboard.overdue"),
      value: stats.inspectionsOverdue,
      icon: Clock,
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      testId: "inspection-tile-overdue",
      onClick: go("overdue"),
    },
    {
      name: t("dashboard.completed"),
      value: stats.inspectionsCompleted,
      icon: CheckCircle2,
      color: "text-success",
      bgColor: "bg-success/10",
      testId: "inspection-tile-completed",
      onClick: go("completed"),
    },
    {
      name: t("dashboard.cancelled"),
      value: stats.inspectionsCancelled,
      icon: FileX,
      color: "text-muted-foreground",
      bgColor: "bg-muted",
      testId: "inspection-tile-cancelled",
      onClick: go("cancelled"),
    },
    ...(includeUnassigned
      ? [
          {
            name: t("dashboard.unassigned"),
            value: stats.inspectionsUnassigned,
            icon: UserX,
            color: "text-info",
            bgColor: "bg-info/10",
            testId: "inspection-tile-unassigned",
            onClick: go("unassigned"),
          },
        ]
      : []),
  ];
}

export function buildDefectTileDefs(opts: {
  stats: DashboardStats | null | undefined;
  t: Translate;
  navigate: Navigate;
  /** unassigned-Kachel (Dashboard.tsx: QM || tester). */
  includeUnassigned: boolean;
}): RoleStatCardDef[] {
  const { stats, t, navigate, includeUnassigned } = opts;
  if (!stats) return [];
  const open = stats.defectsNotStarted + stats.defectsActive + stats.defectsToVerify;
  const go = (status?: string) => () => navigate(status ? `/defects?status=${status}` : "/defects");

  return [
    {
      name: t("common.total"),
      value: stats.totalDefects,
      icon: AlertTriangle,
      color: "text-foreground",
      bgColor: "bg-muted",
      testId: "defect-tile-total",
      onClick: go(),
    },
    {
      name: t("dashboard.openAll"),
      value: open,
      icon: AlertCircle,
      color: "text-warning-foreground",
      bgColor: "bg-warning/15 dark:bg-warning/20",
      testId: "defect-tile-open",
      onClick: go("openAll"),
    },
    {
      name: t("dashboard.notStarted"),
      value: stats.defectsNotStarted,
      icon: AlertTriangle,
      color: "text-warning",
      bgColor: "bg-warning/10",
      testId: "defect-tile-pending",
      onClick: go("open"),
    },
    {
      name: t("dashboard.inProgress"),
      value: stats.defectsActive,
      icon: Play,
      color: "text-primary",
      bgColor: "bg-primary/10",
      testId: "defect-tile-in_progress",
      onClick: go("in_progress"),
    },
    {
      name: t("dashboard.overdue"),
      value: stats.defectsOverdue,
      icon: Clock,
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      testId: "defect-tile-overdue",
      onClick: go("overdue"),
    },
    {
      name: t("dashboard.completed"),
      value: stats.defectsCompleted,
      icon: CheckCircle2,
      color: "text-success",
      bgColor: "bg-success/10",
      testId: "defect-tile-completed",
      onClick: go("completed"),
    },
    {
      name: t("dashboard.cancelled"),
      value: stats.defectsCancelled,
      icon: FileX,
      color: "text-muted-foreground",
      bgColor: "bg-muted",
      testId: "defect-tile-cancelled",
      onClick: go("cancelled"),
    },
    ...(includeUnassigned
      ? [
          {
            name: t("dashboard.unassigned"),
            value: stats.defectsUnassigned,
            icon: UserX,
            color: "text-info",
            bgColor: "bg-info/10",
            testId: "defect-tile-unassigned",
            onClick: go("unassigned"),
          },
        ]
      : []),
  ];
}
